import type { PersistedState } from "redux-persist";

import type {
    IReactionsPersistentState,
    IUserReactionsPersistentState,
} from "../reducers/persistent/reactions";

type ApplicationPersistedState = NonNullable<PersistedState> & {
    persistent?: {
        reactions?: unknown;
        [key: string]: unknown;
    };
};

const isLegacyReactionState = (
    value: unknown
): value is IUserReactionsPersistentState => {
    if (!value || typeof value !== "object") return false;
    const candidate = value as Partial<IUserReactionsPersistentState>;
    return (
        Array.isArray(candidate.reactions) &&
        Array.isArray(candidate.multiStepReactions)
    );
};

const isUserScopedReactionState = (
    value: unknown
): value is IReactionsPersistentState => {
    if (!value || typeof value !== "object" || !("byUser" in value)) {
        return false;
    }

    const byUser = (value as { byUser?: unknown }).byUser;
    return (
        !!byUser &&
        typeof byUser === "object" &&
        Object.values(byUser).every(isLegacyReactionState)
    );
};

/** Drops the former shared workspace while preserving valid user-scoped data. */
export const migration20260730UserScopedReactions = (
    rawState: PersistedState
): PersistedState => {
    const state = rawState as ApplicationPersistedState | undefined;
    if (!state?.persistent) return rawState;

    const persistent = { ...state.persistent };
    const existing = persistent.reactions;
    const reactions = isUserScopedReactionState(existing)
        ? existing
        : { byUser: {} };

    return {
        ...state,
        persistent: {
            ...persistent,
            reactions,
        },
    } as PersistedState;
};
